import React, { useState, useEffect } from 'react'; // Added useState, useEffect
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle, LinkIcon } from 'lucide-react'; // Added LinkIcon
import { usePmt3ProblemsDashboardData } from '@/hooks/usePmt3ProblemsDashboardData'; // Reusing hook which includes OOS data
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { doc, getDoc } from 'firebase/firestore'; // Added doc, getDoc
import { db } from '@/config/firebase'; // Added db

// Reusable fetchClientUrl function (copied for now, consider moving to shared util)
const fetchClientUrl = async (companyName: string, docId: string): Promise<string | null> => {
    if (!companyName || !docId) return null;
    try {
        // Sanitize docId before using it in the path - crucial for keys with spaces/periods
        const sanitizedDocId = docId.replace(/\//g, '_').replace(/\./g, '_');
        const urlDocRef = doc(db, 'pmt2_clienturldatabase', companyName, 'items', sanitizedDocId);
        const urlDocSnap = await getDoc(urlDocRef);
        return urlDocSnap.exists() ? urlDocSnap.data()?.value ?? null : null;
    } catch (error) {
        console.error(`Error fetching client URL for ${docId}:`, error);
        return null;
    }
};

const OutOfStocksDashboard: React.FC = () => {
    const { selectedCompany, selectedProduct } = usePMT3Selection();
    // We reuse the ProblemsDashboard hook as it now fetches OutOfStock data too
    const { data, isLoading, error } = usePmt3ProblemsDashboardData(selectedCompany);
    const [dailyInventoryHistoryUrl, setDailyInventoryHistoryUrl] = useState<string | null>(null); // State for URL

    const outOfStockData = data?.outOfStockData; // Extract the relevant data

    // Fetch Daily Inventory History URL when company changes
    useEffect(() => {
        const fetchUrl = async () => {
            if (selectedCompany) {
                // Use the correct document ID for the URL
                const url = await fetchClientUrl(selectedCompany, '93_ Daily Inventory History');
                setDailyInventoryHistoryUrl(url);
            } else {
                setDailyInventoryHistoryUrl(null); // Reset if no company selected
            }
        };
        fetchUrl();
    }, [selectedCompany]);


    const formatPercentage = (value: any): string => {
        const num = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(num) || num === 0) return '0%'; // Return 0% if zero or invalid
        // Ensure multiplication happens correctly for percentage display
        return `${Math.round(num * 100)}%`;
    };

    const getValue = (field: string): number => {
         const rawValue = outOfStockData?.[field];
         // Use parseFloat for potentially decimal numbers stored as strings, default to 0
         const num = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
         // Return 0 if NaN, otherwise return the number
         return isNaN(num) ? 0 : num;
    };

    const periods = [7, 28, 84, 365];

    if (error) {
        // Apply width constraint here too
        return <div className="max-w-md mx-auto"><Card><CardHeader><CardTitle className="text-2xl font-bold">Out of Stocks</CardTitle></CardHeader><CardContent><p className="text-red-600 text-sm">Error: {error.message}</p></CardContent></Card></div>;
    }

     if (!selectedProduct && !isLoading) {
         return (
            // Constrain width even when no product selected
            <div className="max-w-md mx-auto">
                <Card>
                    <CardHeader className="p-4">
                        {/* Wrap Title and Icon Link in a flex container */}
                        <div className="flex items-center gap-2">
                            <CardTitle className="text-2xl font-bold">Out of Stocks</CardTitle>
                            {dailyInventoryHistoryUrl && (
                                 <a
                                    href={dailyInventoryHistoryUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Daily Inventory History Sheet"
                                    className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center"
                                 >
                                    <LinkIcon size={18} />
                                 </a>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-gray-500">Select a product to view Out of Stock data.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Main component structure wrapped for width constraint
    return (
        <div className="max-w-md mx-auto"> {/* Constrain width and center */}
            <Card>
                <CardHeader className="p-4">
                     {/* Wrap Title and Icon Link in a flex container */}
                    <div className="flex items-center gap-2">
                        <CardTitle className="text-2xl font-bold">Out of Stocks</CardTitle>
                        {dailyInventoryHistoryUrl && (
                             <a
                                href={dailyInventoryHistoryUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Daily Inventory History Sheet"
                                className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center"
                             >
                                <LinkIcon size={18} />
                             </a>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="p-0"> {/* Remove padding for table */}
                     {isLoading && !outOfStockData ? (
                         // Updated Skeleton for 3 columns
                         <Table>
                             <TableHeader>
                                 <TableRow>
                                     <TableHead className="w-1/2 px-4 py-2"><Skeleton className="h-4 w-3/4" /></TableHead>
                                     <TableHead className="w-1/4 px-4 py-2 text-right"><Skeleton className="h-4 w-1/2 ml-auto" /></TableHead>
                                     <TableHead className="w-1/4 px-4 py-2 text-right"><Skeleton className="h-4 w-1/2 ml-auto" /></TableHead>
                                 </TableRow>
                             </TableHeader>
                             <TableBody>
                                 {[...Array(4)].map((_, i) => (
                                     <TableRow key={`skel-${i}`}>
                                         <TableCell className="px-4 py-2"><Skeleton className="h-4 w-3/4" /></TableCell>
                                         <TableCell className="px-4 py-2 text-right"><Skeleton className="h-4 w-1/2 ml-auto" /></TableCell>
                                         <TableCell className="px-4 py-2 text-right"><Skeleton className="h-4 w-1/2 ml-auto" /></TableCell>
                                     </TableRow>
                                 ))}
                             </TableBody>
                         </Table>
                     ) : !outOfStockData && !isLoading ? (
                         <div className="p-4 text-sm text-gray-500">No Out of Stock data available for this ASIN.</div>
                     ) : (
                        // Single Table for 3-column layout
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-1/2 px-4 py-2">Period</TableHead>
                                    <TableHead className="w-1/4 px-4 py-2 text-right">#</TableHead>
                                    <TableHead className="w-1/4 px-4 py-2 text-right">%</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {periods.map((period) => {
                                    const numField = `out_of_stock_num_last_${period}`;
                                    const pctField = `out_of_stock_pct_last_${period}`;
                                    const numValue = getValue(numField);
                                    // Pass raw decimal value to formatPercentage
                                    const pctValueFormatted = formatPercentage(getValue(pctField));

                                    // Conditional Formatting Logic
                                    const isZero = numValue === 0;
                                    // Green if zero, Red if greater than zero
                                    const valueColor = isZero
                                        ? 'text-green-600 dark:text-green-500'
                                        : 'text-red-600 dark:text-red-500';

                                    return (
                                        <TableRow key={period}>
                                            <TableCell className="font-medium px-4 py-2">{`Last ${period} Days`}</TableCell>
                                            {/* Apply color to both # and % cells */}
                                            <TableCell className={`text-right px-4 py-2 font-medium ${valueColor}`}>{numValue}</TableCell>
                                            <TableCell className={`text-right px-4 py-2 font-medium ${valueColor}`}>{pctValueFormatted}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                     )}
                </CardContent>
            </Card>
        </div>
    );
};

export default OutOfStocksDashboard;